import React from 'react';
import moment from 'moment';
import _ from 'lodash';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Pagination,
  Typography,
  Tooltip,
} from '@mui/material';
import CircularProgress from '@mui/material/CircularProgress';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import ErrorIcon from '@mui/icons-material/Error';

import { useFieldBulletinBoards } from '../../services/fieldBulletinBoard/FieldBulletinBoardsContext';
import _masterSlideLoader from '../../utilities/masterSlideLoader';
import { messageUtility } from '../../utilities/messageUtility';
import {
  IMPORTANCE_TYPE,
  DATE_TIME_FORMAT,
  REQUEST_PARAMS,
} from '../../utilities/constants';
import styles from '../../styles/componentStyle/fieldBulletinBoard/FieldBulletinBoardList.scss';
import { FieldBulletinBoardPublicationStatusChip } from './FieldBulletinBoardPublicationStatusChip';
import { isPartnerCompanyRoleId, getRoleNameByRoleId } from '../../utilities/roleUtility';
import LocalStorageService from '../../services/utilities/LocalStorageService';
import { useViewportSize } from '../../utilities/useViewPort';

export function FieldBulletinBoardsTable() {
  const {
    getTotal,
    getBulletinBoardList,
    getPage,
    setPage,
    getIsLoading,
    getFixedSearchParam,
  } = useFieldBulletinBoards();
  const total = getTotal();
  const { viewportHeight } = useViewportSize();

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const limit = REQUEST_PARAMS.parentLimit;
  const roleId = LocalStorageService.getRoleId();
  const isPartnerCompanyUser = isPartnerCompanyRoleId(roleId);

  return (
    <Paper
      sx={{ padding: '16px', margin: '16px', height: `calc(${viewportHeight}px - 290px)` }}
      elevation={1}
    >
      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Typography sx={{ fontWeight: 'bold', fontSize: '24px' }}>
            {total === 0
              ? 0
              : `${(getPage() - 1) * limit + 1}-${Math.min(
                  getPage() * limit,
                  total
                )}`}
          </Typography>
          <Typography sx={{ fontSize: '14px', paddingTop: '10px' }}>
            /{total}件
          </Typography>
        </Box>

        <Pagination
          count={Math.ceil(total / limit)}
          onChange={handleChangePage}
          page={getPage()}
          showFirstButton
          showLastButton
        />
      </Box>
      <TableContainer sx={{ height: 'calc(100% - 20px)' }}>
        <Table
          stickyHeader
          aria-label='sticky table'
          size='small'
          className={styles.table}
        >
          <TableHead className='bulletin-board-table-header'>
            <TableRow>
              {isPartnerCompanyUser && <TableCell />}
              <TableCell />
              <TableCell>{messageUtility.getMessage('I-000-165-26')}</TableCell>
              <TableCell />
              {!isPartnerCompanyUser && <TableCell />}
              <TableCell>{messageUtility.getMessage('I-000-164-06')}</TableCell>
              {!isPartnerCompanyUser && (
                <TableCell sx={{ width: '250px' }}>
                  {messageUtility.getMessage('I-000-164-11')}
                </TableCell>
              )}
            </TableRow>
          </TableHead>
          <TableBody className='bulletin-board-table-body'>
            {getIsLoading() ? (
              <TableRow sx={{ '& td': { border: 0 } }}>
                <TableCell colSpan={6} sx={{ textAlign: 'center' }}>
                  <CircularProgress />
                </TableCell>
              </TableRow>
            ) : total === 0 ? (
              <TableRow>
                <TableCell colSpan={6} sx={{ textAlign: 'center' }}>
                  {messageUtility.getMessage('I-000-005')}
                </TableCell>
              </TableRow>
            ) : (
              _.map(getBulletinBoardList(), row => (
                <TableRow
                  key={row.id}
                  sx={theme => ({
                    cursor: 'pointer',
                    '&:hover': {
                      backgroundColor: theme.palette.action.hover,
                    },
                  })}
                  onClick={() =>
                    _masterSlideLoader.show('FieldBulletinBoardDetail', {
                      state: {
                        id: row.id,
                        fieldBulletinBoardSearchParam: getFixedSearchParam(),
                      },
                    })
                  }
                >
                  {isPartnerCompanyUser && (
                    <TableCell>
                      <div className="icon">
                        {
                          row.already_read ? 
                            <span className="icon__text icon-already-read">既読</span> : 
                            <span className="icon__text icon-unread">未読</span>
                        }
                      </div>
                    </TableCell>
                  )}
                  <TableCell>
                    {row.importance_type === IMPORTANCE_TYPE.CHECK && (
                      <ErrorIcon sx={{ fill: 'red' }} />
                    )}
                  </TableCell>
                  <TableCell>
                    <Tooltip title={row.title} placement='bottom'>
                      <Box>{row.title}</Box>
                    </Tooltip>
                  </TableCell>
                  <TableCell>
                    {row.has_attached_files && (
                      <AttachFileIcon
                        sx={theme => ({
                          fill: theme.palette.primary.main,
                          transform: 'rotate(45deg)',
                        })}
                      />
                    )}
                  </TableCell>
                  {!isPartnerCompanyUser && (
                    <TableCell>
                      <FieldBulletinBoardPublicationStatusChip
                        bulletinBoardPublicationStatusId={
                          row.bulletin_board_publication_status
                        }
                      />
                    </TableCell>
                  )}
                  <TableCell>
                    {moment(row.publication_start_at).format(
                      DATE_TIME_FORMAT.slashDateFormat
                    )}
                  </TableCell>
                  {!isPartnerCompanyUser && (
                    <TableCell>
                      <Tooltip title={row.created_user_name} placement='bottom'>
                        <Box>{row.created_user_name}</Box>
                      </Tooltip>
                      <Box component="span" sx={{fontSize: "13px"}}>{getRoleNameByRoleId(row.created_user_role)}</Box>
                    </TableCell>
                  )}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
}



// WEBPACK FOOTER //
// ./src/components/fieldBulletinBoard/FieldBulletinBoardsTable.jsx